import { useEffect, useState, useSyncExternalStore } from 'react'
import { MetricsBar } from './MetricsBar'
import { ContributorActivity } from './ContributorActivity'
import { FlowBand } from './FlowBand'
import { ProjectSection } from './ProjectSection'
import { ThemeToggle } from './ThemeToggle'
import { RateLimitBanner } from './RateLimitBanner'
import { YouTubeBackground } from './YouTubeBackground'
import { VideoSetup } from './VideoSetup'
import { AudioToggle } from './AudioToggle'
import { CastSetup } from './CastSetup'
import { subscribeVideo, getVideoIds } from '../video'
import { useSlots } from '../useSlots'
import { randomShift, nowHourInChile, shouldDim } from '../oled'
import { useViewportTier } from '../useViewportTier'

const PIXEL_SHIFT_INTERVAL_MS = 30_000
const PIXEL_SHIFT_MAX_PX = 5
const NIGHT_CHECK_INTERVAL_MS = 60_000

export function Dashboard() {
  const ids = useSyncExternalStore(subscribeVideo, getVideoIds, () => [] as string[])
  const tier = useViewportTier()
  const slots = useSlots()
  const [shift, setShift] = useState({ x: 0, y: 0 })
  const [dimming, setDimming] = useState(() => shouldDim(nowHourInChile()))

  // Pixel shift: cada 30s mueve todo ±5px con transición CSS de 2s. Movimiento
  // orgánico, imperceptible, sin scale (que causaba blur en texto). Rota la
  // posición de cada píxel para evitar burn-in OLED.
  useEffect(() => {
    const t = setInterval(() => setShift(randomShift(PIXEL_SHIFT_MAX_PX)), PIXEL_SHIFT_INTERVAL_MS)
    return () => clearInterval(t)
  }, [])

  // Dim nocturno (22:00–08:00 CL). Reevalua cada minuto.
  useEffect(() => {
    const t = setInterval(() => setDimming(shouldDim(nowHourInChile())), NIGHT_CHECK_INTERVAL_MS)
    return () => clearInterval(t)
  }, [])

  const bgClass = ids.length > 0
    ? 'bg-white/70 dark:bg-zinc-950/70 backdrop-blur-sm'
    : 'bg-white dark:bg-zinc-950'

  const shiftStyle = {
    '--shift-x': `${shift.x}px`,
    '--shift-y': `${shift.y}px`,
  } as React.CSSProperties

  return (
    <>
      <YouTubeBackground />
      <div
        data-tier={tier}
        data-dimming={dimming}
        className={`h-dvh w-full overflow-hidden flex flex-col text-zinc-900 dark:text-zinc-100 wiphub-oled-shift ${bgClass} ${dimming ? 'wiphub-night-dim' : ''}`}
        style={shiftStyle}
      >
        <RateLimitBanner />
        <MetricsBar />
        <ContributorActivity />
        <FlowBand />
        {/* Board de desarrollo: grid 2×2 de 4 celdas. Cada celda muestra un repo
            y rota de forma escalonada (una a la vez). Cada celda scrollea
            independiente; overflow-hidden en el grid evita doble scrollbar. */}
        <main
          data-testid="board-grid"
          className="flex-1 min-h-0 grid grid-cols-2 grid-rows-2 overflow-hidden"
        >
          {/* slot.repo.slug en la key es LOAD-BEARING: al rotar el carrusel el
              repo del slot cambia → React re-monta ProjectSection → su estado
              local `expanded` (colapso de merges) vuelve a colapsado. No quitar. */}
          {slots.map((slot, idx) => (
            <div
              key={`${slot.mode}-${idx}-${slot.repo.slug}`}
              data-testid={`grid-cell-${idx}`}
              className="min-h-0 overflow-y-auto border-b-2 border-r-2 border-zinc-900"
            >
              <ProjectSection
                repo={slot.repo}
                mode={slot.mode}
                slotIndex={slot.slotIndex}
                carouselTotal={slot.carouselTotal}
                isFirst
              />
            </div>
          ))}
        </main>
        <footer className="fixed bottom-2 right-2 4k:bottom-4 4k:right-4 flex items-center gap-1">
          <AudioToggle />
          <CastSetup />
          <VideoSetup />
          <ThemeToggle />
        </footer>
      </div>
    </>
  )
}
